import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { login, register } from "../redux/slices/userSlice";
import TextDivider from "../components/divider/TextDivider";
import AuthForm from "../components/auth/AuthForm";
import SSOOptions from "../components/auth/SSOOptions";
import { SPACING } from "../theme/constants";

export default function Authentication() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode"); // 'login' or 'signup'

  // Scroll to top when auth page is opened (prevents scrolled view from Landing page)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const handleLogin = async (loginForm) => {
    const res = await dispatch(login(loginForm));
    if (res.type === "user/login/fulfilled") {
      // Check for pending invite token from pre-login invite flow
      const pendingToken = localStorage.getItem("pendingInviteToken");
      if (pendingToken) {
        localStorage.removeItem("pendingInviteToken");
        navigate(`/invite/${pendingToken}`);
        return { success: true };
      }
      const role = res.payload?.data?.user?.role;
      navigate(role === "ADMIN" ? "/admin" : "/dashboard");
      return { success: true };
    }
    return {
      success: false,
      message: res.payload?.message || "Invalid credentials. Please try again.",
    };
  };

  const handleSignup = async (signupForm) => {
    const res = await dispatch(register(signupForm));
    if (res.type === "user/register/fulfilled") {
      // Check for pending invite token from pre-login invite flow
      const pendingToken = localStorage.getItem("pendingInviteToken");
      if (pendingToken) {
        localStorage.removeItem("pendingInviteToken");
        navigate(`/invite/${pendingToken}`);
        return { success: true };
      }
      navigate("/dashboard");
      return { success: true };
    }
    return {
      success: false,
      message:
        res.payload?.message ||
        "Registration failed. Please check your inputs.",
      errors: res.payload?.errors,
    };
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      {/* Back button – part of normal flow so it never overlaps the heading */}
      <Box sx={{ px: SPACING.lg, pt: SPACING.md }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/")}
          disableElevation
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Back to Home
        </Button>
      </Box>

      {/* Centered content occupies remaining vertical space */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: SPACING.lg,
          pb: SPACING.lg,
        }}
      >
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Welcome to WP Spark!
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ mb: SPACING.lg }}
        >
          Choose how you want to sign in
        </Typography>

        {/* Single Column Layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: SPACING.md,
            width: "100%",
            maxWidth: 500,
          }}
        >
          {/* Login / Signup Form */}
          <AuthForm
            onLogin={handleLogin}
            onSignup={handleSignup}
            defaultMode={mode}
          />

          {/* Divider */}
          {/* <TextDivider chip="OR" textAlign="center" sx={{ width: "100%" }} /> */}

          {/* SSO Options – keep intact */}
          {/* <SSOOptions /> */}
        </Box>
      </Box>
    </Box>
  );
}
